<template>
  <div
    v-if="currentImage"
    :class="{
      'image-viewer-container': true,
      'image-viewer-container--dark': inCalculatorMode
    }"
  >
    <div class="image-viewer">
      <div
        v-if="viewerMode === 'viewer'"
        class="image-viewer__header height-element"
      >
        <div class="image-viewer__header-close">
          <a
            :class="{ 'saving-disabled': savingImage }"
            @click.prevent="closeViewer"
          >
            <UseSvg
              class="icon"
              name="close"
            />
          </a>
        </div>
        <slot />
      </div>
      <div
        v-if="inCalculatorMode"
        class="image-viewer__calculator-header height-element"
      >
        <HeaderBar :modifiers="['no-padding', 'transparent', 'more-height']">
          <a
            class="button button--small"
            @click="resetSideBar"
          >
            <UseSvg
              class="icon"
              name="arrow_left"
            />
          </a>
          <span class="image-viewer__navigate-back-text">
            {{ $t("issues.damage_assessment") }}
          </span>
          <template #right>
            <a
              class="button"
              @click="resetSideBar"
            >
              {{ $t("general.cancel") }}
            </a>
            <a
              class="button button--inverse"
              :class="{ 'button--disabled': !damageRating }"
              @click="openPopup($t('issues.result_apply_confirmation'))"
            >
              {{ $t("general.apply") }}
            </a>
          </template>
        </HeaderBar>
      </div>
      <div class="image-viewer__body-container image-viewer__body-container--rounded">
        <div class="image-viewer__image-container">
          <div
            ref="parentImageContainer"
            class="image-viewer__main"
          >
            <div class="image-viewer__image-edit-heading typo-tiny">
              <span v-if="savingImage">{{ $t("general.image_being_saved") }}</span>
              <span v-else-if="!calculatorSelectMode && !inLocalCalculatorMode">
                {{ $t("general.click_image_to_edit") }}
              </span>
            </div>
            <div
              ref="imageContainer"
              class="image-viewer__marker-images"
            >
              <img
                ref="originalImageRef"
                :class="{ 'saving-disabled': savingImage }"
                crossorigin="anonymous"
                :onload="setSourceImage()"
                :src="originalImageSrc(currentImage)"
                @load="originalImageLoaded = true"
              >
              <img
                ref="imageRef"
                :class="{ 'saving-disabled': savingImage }"
                crossorigin="anonymous"
                :src="imageSrc(images[selectedIndex])"
                style="position: absolute"
                @click="showMarkerArea"
                @load="annotatedImageLoaded = true"
              >
            </div>
          </div>
          <div
            v-if="viewerMode === 'viewer'"
            class="image-viewer__thumbnail-container height-element"
          >
            <div class="image-viewer__scroller">
              <ScrollCounter
                :class="{ 'saving-disabled': savingImage }"
                :images="props.images"
                :selectedIndex="selectedIndex"
                @set-current-image="emitCurrentImage"
              />
            </div>
            <div
              ref="thumbnailContainer"
              class="image-viewer__thumbnails"
            >
              <div
                v-for="(image, index) in images"
                :key="image.id"
                :ref="(img) => thumbnailImages.push(img)"
                class="image-viewer__thumbnail"
              >
                <img
                  :class="{
                    selected: index === selectedIndex,
                    'saving-disabled': savingImage,
                  }"
                  :src="thumbSrc(image)"
                  @click="!savingImage ? $emit('update:currentImage', image) : ''"
                >
              </div>
            </div>
          </div>
          <div class="image-viewer__mobile-options height-element">
            <button
              class="button button-dark button-show-details"
              type="button"
              @click="setMobileDetailView"
            >
              <span class="typo-small">{{ $t("general.show_details") }}</span>
              <div class="icon icon--small">
                <IEncircled />
              </div>
            </button>
          </div>
        </div>
        <div class="image-viewer__sidebar show-for-medium">
          <TheSidebar
            v-if="currentOperation && issue"
            :currentImage="images[selectedIndex]"
            :currentOperation="currentOperation"
            :damageRating="damageRating"
            :images="images"
            :issue="issue"
            :issuePath="issuePath"
            :ratio="ratio"
            :selectedTabIndex="selectedTabIndex"
            :viewerMode="viewerMode"
            @calculate-damage-rating="calculateDamageRating"
            @calculate-local-damage-rating="calculateLocalDamageRating"
            @clear-damage-rating="clearDamageRating"
            @reset-damage-rating="damageRating = null"
            @set-annotation-type="setImageAnnotationType"
            @set-selected-tab-index="setSelectedTabIndex"
            @set-viewer-mode="setViewerMode"
            @update-image="updateImage"
          />
        </div>
        <Popper
          v-model:open="mobileDetailView"
          :modifiers="['cover']"
          placement="top-end"
        >
          <div class="image-viewer__mobile-content">
            <div class="image-viewer__mobile-header">
              <FilterMobileHeader
                v-if="viewerMode === 'viewer'"
                :label="$t('general.back_to_image')"
                @toggle="setMobileDetailView"
              >
                <a
                  :class="{ 'saving-disabled': savingImage }"
                  @click.prevent="closeViewer"
                >
                  <UseSvg
                    class="icon"
                    name="close"
                  />
                </a>
              </FilterMobileHeader>
              <HeaderBar
                v-else
                :modifiers="['transparent', 'more-height']"
              >
                <a
                  class="button button--small"
                  @click="resetSideBar"
                >
                  <UseSvg
                    class="icon"
                    name="arrow_left"
                  />
                </a>
                <template #right>
                  <a class="button">{{ $t('general.previous') }}</a>
                  <a class="button button--inverse">{{ $t('general.next') }}</a>
                </template>
              </HeaderBar>
            </div>
            <TheSidebar
              v-if="currentOperation && issue"
              :currentImage="images[selectedIndex]"
              :currentOperation="currentOperation"
              :damageRating="damageRating"
              :images="images"
              :issue="issue"
              :issuePath="issuePath"
              :ratio="ratio"
              :selectedTabIndex="selectedTabIndex"
              :viewerMode="viewerMode"
              @calculate-damage-rating="calculateDamageRating"
              @calculate-local-damage-rating="calculateLocalDamageRating"
              @clear-damage-rating="clearDamageRating"
              @reset-damage-rating="damageRating = null"
              @set-annotation-type="setImageAnnotationType"
              @set-selected-tab-index="setSelectedTabIndex"
              @set-viewer-mode="setViewerMode"
              @update-image="updateImage"
            />
          </div>
        </Popper>
      </div>
      <div class="image-viewer__hidden-icons">
        <DeleteMarker class="delete-marker-icon" />
        <UndoMarker class="undo-marker-icon" />
        <SelectMarker class="select-marker-icon" />
        <ZoomMarker class="zoom-marker-icon" />
        <ZoomOutMarker class="zoom-out-marker-icon" />
        <FrameMarker class="frame-marker-icon" />
        <CalloutMarker class="callout-marker-icon" />
      </div>
    </div>
  </div>
  <Dialog
    v-if="dialogIsOpen"
    :message="popupErrorMessage"
    :title="$t('issues.damage_calculator')"
    @dismiss-dialog="dialogIsOpen = false"
  >
    <template #actions>
      <button
        aria-label="confirm"
        class="button button--inverse"
        type="button"
        @click="applyDamageRating"
      >
        {{ $t('general.yes') }}
      </button>
    </template>
  </Dialog>
</template>

<script setup>
import * as markerjs2 from 'markerjs2';
import { DamageMarker } from '@/markers/damage_marker';
import { useRoute } from 'vue-router';
import { screenAtLeast } from '@/functions/screen_width';
import { nextTick, ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { annotateImage, saveAnnotatedImage } from '@/functions/image_viewer/annotate_image';
import { lookupOverallDamageRating, lookupLocalDamageRating } from '@/functions/simm/lookup_damage_rating';
import I18n from 'i18n-js';
import useUploader from '@/vendor/use_uploader';
import useEmitter from '@/functions/use_emitter';
import FilterMobileHeader from '@/components/generic/date_filter_mobile_header.vue';
import Popper from '@/components/generic/popper.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import ScrollCounter from '@/components/image_viewer/scroll_counter.vue';
import TheSidebar from '@/components/image_viewer/the_sidebar.vue';
import Dialog from '@/components/generic/dialog.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import IEncircled from '@/images/i_encircled-16.svg';
import DeleteMarker from '@/images/delete-marker-icon.svg';
import UndoMarker from '@/images/undo-marker-icon.svg';
import SelectMarker from '@/images/select-marker-icon.svg';
import ZoomMarker from '@/images/zoom-marker-icon.svg';
import ZoomOutMarker from '@/images/zoom-out-marker-icon.svg';
import FrameMarker from '@/images/frame-marker-icon.svg';
import CalloutMarker from '@/images/callout-marker-icon.svg';

const emit = defineEmits([
  'annotateImage',
  'applyDamageRating',
  'clearDamageRating',
  'update:currentImage',
  'updateImage',
]);

const props = defineProps({
  /** The issue path used to retrieve the image */
  issuePath: { type: String, required: true },
  /** A list of images related to the issue */
  images: { type: Array, default: () => [] },
  /** The currently selected image in the images array */
  currentImage: { type: Object, default: null },
  /** The current operation */
  currentOperation: { type: Object, default: null },
  /** The current issue */
  issue: { type: Object, required: true }
});

const MARKER_BACKGROUND_COLOR = '#454445';
const MARKER_ICONS = ['delete', 'select', 'undo', 'zoom', 'zoom-out'];
const MARKER_TOOL_ICONS = ['frame', 'callout'];

const thumbnailContainer = ref(null);
const thumbnailImages = ref([]);
const mobileDetailView = ref(false);
const originalImageRef = ref(null);
const imageRef = ref(null);
const imageContainer = ref(null);
const parentImageContainer = ref(null);
const originalImageLoaded = ref(false);
const annotatedImageLoaded = ref(false);
const savingImage = ref(false);
const viewerMode = ref('viewer');
const imageAnnotationType = ref(null);
const selectedTabIndex = ref(0);
const ratio = ref(null);
const damageRating = ref(null);
const resetMode = ref(false);
const dialogIsOpen = ref(false);
const popupErrorMessage = ref(null);
const structure = ref(false);
const localDamageData = ref(null);

const emitter = useEmitter();
const uploader = useUploader();
const route = useRoute();

let targetRoot, markerArea;

const selectedIndex = computed(() => {
  if (!props.currentImage) return;

  return props.images.findIndex((image) => {
    if (image['issue_id']) {
      return image['image_blob']['id'] === props.currentImage['image_blob']['id'] || image['id'] === props.currentImage['id'];
    } else {
      return image['id'] === props.currentImage['id'];
    }
  });
});

/**
 * An image cannot be annotated when on the Damage calculator tab,
 * but the calculations have not been started
 */
const calculatorSelectMode = computed(() => {
  return imageAnnotationType.value === 'damage' && viewerMode.value === 'viewer';
});

/**
 * Determine whether the damage calculator has been started
 */
const inCalculatorMode = computed(() => {
  return ['local', 'overall'].includes(viewerMode.value);
});

/**
 * Determine whether the overall damage calculator has been started
 */
const inOverallCalculatorMode = computed(() => {
  return viewerMode.value === 'overall';
});

/**
 * Determine whether the local damage calculator has been started
 */
const inLocalCalculatorMode = computed(() => {
  return viewerMode.value === 'local';
});

/**
 * Determine whether the damage calculator has been started
 */
const nonCalculatorMode = computed(() => {
  return !calculatorSelectMode.value && !inCalculatorMode.value;
});

/**
 * Has annotation markup on the image
 */
const hasAnnotationMarkup = computed(() => {
  return props.images[selectedIndex.value]['markup'] &&
    Object.keys(props.images[selectedIndex.value]['markup']).length > 0;
});

/**
 * Has damage marker annotations on the image
 */
const hasDamageMarkerMarkup = computed(() => {
  return props.images[selectedIndex.value]['damage_marker_markup'] &&
    Object.keys(props.images[selectedIndex.value]['damage_marker_markup']).length > 0;
});

onMounted(() => {
  window.addEventListener('resize', setImageViewerHeight, { passive: true });
  window.addEventListener('keydown', scrollThumbnails, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', setImageViewerHeight);
  window.removeEventListener('keydown', scrollThumbnails);
});

watch(
  () => props.currentImage,
  async () => {
    originalImageLoaded.value = false;
    annotatedImageLoaded.value = false;
    thumbnailImages.value = [];
    selectedTabIndex.value = 0;
    damageRating.value = null;
    ratio.value = null;
    setCurrentImageRatioAndDamage();
    await nextTick();

    // Set to limit image viewer flex container height to not grow past page height
    setImageViewerHeight('closeMarker');
    // Bring out-of-view thumbnails on the carousel back into view when selected
    adjustCarouselScrollPosition();
    // Close marker if we are changing the selected image
    if (markerArea) {
      markerArea.close();
      markerArea = null;
    }
  }
);

watch(
  () => [originalImageLoaded.value, annotatedImageLoaded.value],
  () => {
    if (originalImageLoaded.value && annotatedImageLoaded.value) {
      scaleSmallImages();

      if (calculatorSelectMode.value && resetMode.value) {
        showMarkerArea();
      }
    }
  }
);

watch(
  () => viewerMode.value,
  () => {
    if (inCalculatorMode.value) {
      // TODO: Remove this temp fix to render Marker successfully when testing
      if (props.issuePath !== '/testIssuePath') {
        if (inOverallCalculatorMode.value) showMarkerArea();
        setImageViewerHeight('closeMarker');
      }
    }
  }
);

/** When updating image annotations for damage markers, reload Marker */
watch(
  () => props.images,
  () => {
    setCurrentImageRatioAndDamage();

    if (nonCalculatorMode.value) return;

    if (markerArea) {
      markerArea.close();
      markerArea = null;
    }
    showMarkerArea();
  }
);

function setCurrentImageRatioAndDamage() {
  if (props.images[selectedIndex.value]?.calculation_type === 'overall') {
    ratio.value = damageRatio(props.images[selectedIndex.value]['damage_marker_markup']['markers'][0]);
    damageRating.value = lookupOverallDamageRating(props.images[selectedIndex.value]['damaged_structure'], ratio.value);
  } else if ((props.images[selectedIndex.value]?.calculation_type === 'local')) {
    const image = props.images[selectedIndex.value];

    damageRating.value = lookupLocalDamageRating(
      image['deviation'],
      image['deviation_unit'],
      image['thickness'],
      image['thickness_unit']
    );
  }
}

function scrollThumbnails(event) {
  if (!props.currentImage) return;

  if (['input', 'textarea'].includes(event.target.tagName.toLowerCase())) return;

  switch (event.key) {
    case 'ArrowLeft':
      if (selectedIndex.value > 0) {
        emitCurrentImage(props.images[selectedIndex.value - 1]);
      }
      break;
    case 'ArrowRight':
      if (selectedIndex.value < props.images.length - 1) {
        emitCurrentImage(props.images[selectedIndex.value + 1]);
      }
      break;
  }
}

function adjustCarouselScrollPosition() {
  if (!thumbnailImages.value[selectedIndex.value]) return;

  const elementDimensions = thumbnailImages.value[
    selectedIndex.value
  ].getBoundingClientRect();
  const containerDimensions = thumbnailContainer.value.getBoundingClientRect();

  if (elementDimensions.right > containerDimensions.right) {
    const scrollBy = elementDimensions.right - containerDimensions.right + 10;
    thumbnailContainer.value.scrollBy({ left: scrollBy });
  }

  if (elementDimensions.left < containerDimensions.left) {
    const scrollBy = elementDimensions.left - containerDimensions.left - 10;
    thumbnailContainer.value.scrollBy({ left: scrollBy });
  }
}

async function setImageViewerHeight(markerState = 'openMarker') {
  let tempState;

  // Save the marker state, close marker when resizing
  if (markerArea && markerState === 'openMarker') {
    tempState = markerArea.getState();
    markerArea.close();
  }

  if (screenAtLeast('medium')) {
    mobileDetailView.value = false;
    await nextTick();
  }

  const mainView = document.querySelector('.image-viewer');

  if (!mainView) return;

  let elementHeight = 0;
  document.querySelectorAll('.height-element').forEach((element) => {
    elementHeight += element.clientHeight;
  });

  const height = mainView.clientHeight - elementHeight;

  // cater for the bottom of the page having to accommodate a marker panel
  let heightReduction = inOverallCalculatorMode.value ? 95 : 40;

  imageContainer.value.style.height = (height - heightReduction).toString() + 'px';
  imageContainer.value.style.width = '';

  if (inOverallCalculatorMode.value) {
    const h = parentImageContainer.value.clientHeight + 55;
    parentImageContainer.value.style.height = `${h.toString()}px`;
  }

  // Once done resizing, restore marker and re-focus
  if (markerArea && tempState) {
    markerArea.show();
    markerArea.focus();
    markerArea.restoreState(tempState);
  }

  scaleSmallImages();
}

/**
 * Upscale images smaller than both container height and width
 */
function scaleSmallImages() {
  if (
    imageRef.value.naturalHeight < imageContainer.value.clientHeight &&
    imageRef.value.naturalWidth < parentImageContainer.value.clientWidth
  ) {
    const ratio = imageRef.value.naturalHeight / imageContainer.value.clientHeight;

    const adjustedWidth = imageRef.value.naturalWidth / ratio;

    if (!Number.isNaN(adjustedWidth) && adjustedWidth > parentImageContainer.value.clientWidth) {
      imageContainer.value.style.width = parentImageContainer.value.clientWidth.toString() + 'px';
    } else if (!Number.isNaN(adjustedWidth)) {
      imageContainer.value.style.width = adjustedWidth.toString() + 'px';
    }
  }
}

function setMarkerArea() {
  const markerJsArea = new markerjs2.MarkerArea(originalImageRef.value);
  markerJsArea.targetRoot = targetRoot;
  markerJsArea.uiStyleSettings.zIndex = '2000';

  if (nonCalculatorMode.value) {
    markerJsArea.availableMarkerTypes = markerJsArea.ALL_MARKER_TYPES;
    markerJsArea.renderAtNaturalSize = true;
  } else {
    markerJsArea.availableMarkerTypes = [ DamageMarker ];
  }
  markerJsArea.renderImageQuality = 1;
  markerJsArea.uiStyleSettings.zoomButtonVisible = true;
  markerJsArea.uiStyleSettings.zoomOutButtonVisible = true;
  markerJsArea.uiStyleSettings.undoButtonVisible = true;
  markerJsArea.uiStyleSettings.toolbarBackgroundColor = MARKER_BACKGROUND_COLOR;
  return markerJsArea;
}

async function showMarkerArea() {
  // TODO: find a better fix when testing with Marker
  if (props.issuePath === '/testIssuePath' || inLocalCalculatorMode.value) return;

  if (calculatorSelectMode.value) readonlyMarkerArea();

  // Do not open Marker if an image is being saved or damage tool selected but not on step 1 yet
  if (savingImage.value || calculatorSelectMode.value) {
    return;
  }

  // create a marker.js MarkerArea
  markerArea = setMarkerArea();

  // Add a listener for when changes are saved
  markerArea.addEventListener('render', async (event) => {
    // Set top image to be the new rendered (annotated) image
    imageRef.value.src = event.dataUrl;

    if (inOverallCalculatorMode.value) {
      emit('applyDamageRating', damageRating.value, {
        calculation_type: viewerMode.value,
        damaged_structure: structure.value,
        damage_marker_markup: event.state,
      });
      resetSideBar();

      return;
    }

    // Generate annotated image to be saved to backend
    const annotatedImage = annotateImage(event, props.currentImage, originalImageRef.value);

    await saveAnnotatedImage(annotatedImage, uploader, props.issuePath, isShaft.value);

    emit('annotateImage', annotatedImage);

    savingImage.value = false;
  });

  // only allow one marker in the damage calculator view
  markerArea.addEventListener('markercreate', (event) => {
    if (inOverallCalculatorMode.value && markerArea.markers.length > 1) {
      emitter.emit('toast', {
        status: 'error',
        message: I18n.t('general.image_damage_restriction'),
      });
      event.preventDefault();
      markerArea.deleteSelectedMarker();
    }
  });

  // when the state changes (markers are added/deleted) - enable/disable the Next button
  markerArea.addEventListener('statechange', (event) => {
    if (inOverallCalculatorMode.value) {
      damageRating.value = null;
    }

    if (nextStepAvailable(event)) {
      ratio.value = damageRatio(event?.markerArea?.markers[0]);
    } else {
      ratio.value = null;
    }
  });

  markerArea.addEventListener('show', () => {
    // Hide the '✓' and 'x' icons when doing damage calculations
    if (inOverallCalculatorMode.value) {
      document.querySelector('.__markerjs2_toolbar-block:last-of-type').innerHTML = '';
    }
    markerArea.focus();
  });

  // launch marker.js
  markerArea.show();

  replaceMarkerIcons();

  // set the creation to a Damage Marker when opening Marker in damage calculation mode
  if (inOverallCalculatorMode.value && !hasDamageMarkerMarkup.value) {
    markerArea.createNewMarker(DamageMarker);
  }

  // Restore persisted annotations if the image has any
  if (nonCalculatorMode.value && hasAnnotationMarkup.value) {
    markerArea.restoreState(props.images[selectedIndex.value]['markup']);
  }

  // Restore persisted damage marker annotations if any
  if ((inOverallCalculatorMode.value || calculatorSelectMode.value) && hasDamageMarkerMarkup.value) {
    markerArea.restoreState(props.images[selectedIndex.value]['damage_marker_markup']);
  }

  if (viewerMode.value === 'viewer') {
    disableMarkerEditOnSave();
  }
}

/**
 * A read-only marker area for displaying damage calculator results
 */
function readonlyMarkerArea() {
  // create a marker.js MarkerArea
  markerArea = setMarkerArea();

  markerArea.addEventListener('render', async (event) => {
    // Set top image to be the new rendered (annotated) image
    imageRef.value.src = event.dataUrl;
  });

  markerArea.show();

  // Hide Marker toolbars when in read-only mode
  document.querySelectorAll('.__markerjs2_toolbar-block').forEach((element) => { element.style.display = 'none'; } );

  // Restore persisted annotations if the image has any
  if (hasDamageMarkerMarkup.value && calculatorSelectMode.value) {
    markerArea.restoreState(props.images[selectedIndex.value]['damage_marker_markup']);
  }

  markerArea.startRenderAndClose();
}

/**
 * When selecting to save on Marker, we cannot disable the UI as it renders the canvas before
 * firing the 'render' event. This gives the user time to navigate away and leads to lots of
 * unexpected issues. Therefore we are hiding the save div element behind the SVG and attaching
 * a new disable event to a new div behind the SVG, so the UI is disabled before the render is fired.
 */
function disableMarkerEditOnSave() {
  const saveButtonDiv = document.querySelector('div[data-action="render"]');
  const saveButtonSvg = document.querySelector('div[data-action="render"] svg');
  const closeButtonDiv = document.querySelector('div[data-action="close"]');
  const toolbarDiv = document.querySelector('.__markerjs2_toolbar-block:last-of-type');

  let wrappingSvgDiv = document.createElement('div');
  wrappingSvgDiv.appendChild(saveButtonSvg);

  let wrappingSaveDiv = document.createElement('div');
  wrappingSaveDiv.setAttribute('class', '__markerjs2_toolbar_button __markerjs2_toolbar_button_colors');
  wrappingSaveDiv.style.width = '20px';
  wrappingSaveDiv.style.height = '20px';
  wrappingSaveDiv.style.padding = '0';

  saveButtonDiv.setAttribute('class', '');
  saveButtonDiv.appendChild(wrappingSvgDiv);
  wrappingSaveDiv.appendChild(saveButtonDiv);

  let wrappingToolbarDiv = document.createElement('div');
  wrappingToolbarDiv.style.display = 'flex';
  wrappingToolbarDiv.appendChild(wrappingSaveDiv);
  wrappingToolbarDiv.appendChild(closeButtonDiv);
  toolbarDiv.appendChild(wrappingToolbarDiv);

  wrappingSvgDiv.addEventListener('click', () => {
    document.querySelector('.__markerjs2_').style.opacity = '0.0';

    if (viewerMode.value === 'viewer') {
      savingImage.value = true;
    }
  });
}

function setSourceImage() {
  if (!originalImageRef.value) return;
  targetRoot = originalImageRef.value?.parentElement;
}

const isShaft = computed(() => {
  return !route.path.includes('shafts_v2') && route.params.shaftId;
});

function imageSrc(image) {
  if (imageAnnotationType.value === 'damage' && !image.isNew) {
    return originalImageSrc(image);
  }

  if (image.isNew) {
    return props.images[selectedIndex.value]['url'];
  }
  return `/internal${props.issuePath}/issue_images/${image.id}.jpeg?t=${Date.now()}`;
}

/**
 * Only enable the next step if a Damage Marker has been added
 * @param event - last Marker event/state.
 */
function nextStepAvailable(event) {
  if (inOverallCalculatorMode.value) {
    return event?.markerArea?.markers?.length > 0;
  }

  return false;
}

/**
 * Get the damage ratio from the Damage Marker dimensions
 */
function damageRatio(marker) {
  return (Number.parseFloat(marker?.damageLineDistance) / Number.parseFloat(marker?.mainLineDistance)) * 100;
}

function originalImageSrc(image) {
  if (image.isNew && !image.original_image_blob) {
    return props.images[selectedIndex.value]['original_url'] || imageSrc(image);
  } else if (image.isNew && props.images[selectedIndex.value]['original_url']) {
    return props.images[selectedIndex.value]['original_url'];
  }

  return `/internal${props.issuePath}/issue_images/${image.id}.jpeg?type=original`;
}

function thumbSrc(image) {
  if (image.isNew) return image.url;

  return imageSrc(image) + '&variant=thumbnail';
}

/**
 * Replace the matching icons from Marker with custom ones
 * The first list includes standard icons (MARKER_ICONS) with data-action attribute
 * The second list includes marker tools (MARKER_TOOL_ICONS - like lines, text)
 * and have a data-type-name attribute
 *
 * We replace the marker icon with the matching icon from the .image-viewer__hidden-icons div
*/
function replaceMarkerIcons() {
  MARKER_ICONS.forEach((icon) => {
    const newIcon = document.querySelector(`.${icon}-marker-icon`).outerHTML;
    document.querySelector(`.__markerjs2_toolbar_button[data-action="${icon}"]`).innerHTML = newIcon;
  });

  if (nonCalculatorMode.value) {
    MARKER_TOOL_ICONS.forEach((icon) => {
      const newIcon = document.querySelector(`.${icon}-marker-icon`).outerHTML;
      const iconMarkerName = icon.charAt(0).toUpperCase() + icon.slice(1) + 'Marker';
      document.querySelector(`.__markerjs2_toolbar_button[data-type-name="${iconMarkerName}"]`).innerHTML = newIcon;
    });
  }
}

function emitCurrentImage(image) {
  if (!savingImage.value) {
    emit('update:currentImage', image);
  }
}

function closeViewer() {
  selectedTabIndex.value = 0;
  imageAnnotationType.value = '';
  if (!savingImage.value) {
    mobileDetailView.value = false;
    emit('update:currentImage', null);
  }
}

function updateImage(value) {
  emit('updateImage', value);
}

function setImageAnnotationType(annotationType) {
  // Close marker when selecting different tabs
  if (markerArea) {
    markerArea.close();
    markerArea = null;
  }

  imageAnnotationType.value = annotationType;
}

function setSelectedTabIndex(index) {
  selectedTabIndex.value = index;

  if (calculatorSelectMode.value) {
    resetMode.value = true;

    // Don't show marker before the images are loaded - causes errors
    if (originalImageLoaded.value && annotatedImageLoaded.value) {
      showMarkerArea();
    }
  } else {
    if (resetMode.value) {
      // Reset the top image to the annotated one when switching
      imageRef.value.src = imageSrc(props.images[selectedIndex.value]);
      resetMode.value = false;
    }
  }
}

function openPopup(message) {
  dialogIsOpen.value = true;
  popupErrorMessage.value = message;
}

function setViewerMode(mode) {
  viewerMode.value = mode;
  if (viewerMode.value === 'viewer') {
    setCurrentImageRatioAndDamage();
    setImageViewerHeight();
  } else {
    if (markerArea) markerArea.focus();
  }
}

function resetSideBar() {
  if (!props.images[selectedIndex.value]?.calculation_type) {
    damageRating.value = null;
    ratio.value = null;
  } else {
    setCurrentImageRatioAndDamage();
  }
  viewerMode.value = 'viewer';
  imageAnnotationType.value = 'damage';
  if (markerArea && calculatorSelectMode.value) {
    markerArea.close();
    markerArea = null;
  }
  setImageViewerHeight();
}

function applyDamageRating() {
  if (inOverallCalculatorMode.value) {
    markerArea.startRenderAndClose();
  } else if (inLocalCalculatorMode.value) {
    emit('applyDamageRating', damageRating.value, {
      calculation_type: viewerMode.value,
      ...localDamageData.value,
    });
    resetSideBar();
  }
  dialogIsOpen.value = false;
}

function calculateDamageRating(type) {
  structure.value = type;
  damageRating.value = lookupOverallDamageRating(type, ratio.value);
}

function calculateLocalDamageRating({ deviation, deviationUnit, thickness, thicknessUnit }) {
  localDamageData.value = {
    deviation,
    thickness,
    deviation_unit: deviationUnit,
    thickness_unit: thicknessUnit,
  };
  damageRating.value = lookupLocalDamageRating(deviation, deviationUnit, thickness, thicknessUnit);
}

function clearDamageRating() {
  emit('clearDamageRating');
  damageRating.value = null;
  closeViewer();
}

async function setMobileDetailView() {
  mobileDetailView.value = !mobileDetailView.value;

  if (markerArea) markerArea.close();

  if (!mobileDetailView.value) {
    await nextTick();
    setImageViewerHeight();
  }
}
</script>

<style lang="scss" scoped>
@import "@/stylesheets/global";

$issue-view-width: rem(992);
$issue-ratings-width: (
  medium: rem(296),
  large: rem(320),
);
$thumbnail-image: (
  small: (
    width: rem(105),
    height: rem(80),
  ),
  medium: (
    width: rem(116),
    height: rem(87),
  ),
);

.image-viewer-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  min-height: 100%;
  z-index: 1004;
  background-color: map-get($background-color, darkest);

  &--dark {
    background-color: map-get($background-color, dark);
  }
}

.image-viewer {
  display: flex;
  flex-direction: column;
  height: 100vh;
  justify-content: space-between;
  margin: 0 auto;
  max-width: $issue-view-width;

  .saving-disabled {
    opacity: 0.1;
  }

  .button--disabled {
    color: map-get($foreground-color, dark);
  }

  .background-light {
    background-color: map-get($background-color, light);
  }

  &__header {
    padding: bu(3) bu(3) bu(2);
    position: relative;
    text-align: center;
    height: auto;

    p {
      padding: 0 bu(3);
    }

    &-close {
      right: bu(3);
      position: absolute;
      cursor: pointer;
      width: bu(3);

      @include breakpoint(medium) {
        left: bu(3);
      }
    }
  }

  &__calculator-header {
    padding-top: bu(1.6);
  }

  &__body-container {
    display: flex;
    flex-direction: row;
    overflow: auto;
    height: 100%;

    &--rounded {
      border-radius: map-get($border-radius, heavier);
    }
  }

  &__navigate-back-text {
    color: map-get($foreground-color, light);
    padding-left: bu(3);
    font-size: bu(1.875);
  }

  &__image-edit-heading {
    align-items: center;
    display: flex;
    font-weight: 500;
    min-height: bu(5);
    justify-content: center;

    span {
      padding-right: bu(1);
    }
  }

  &__marker-images {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
  }

  &__image-container {
    min-height: 100%;
    width: 100%;
    overflow-x: auto;
    display: flex;
    flex-direction: column;
    user-select: none;
  }

  &__main {
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: map-get($background-color, light);
    margin-right: bu(0.05);

    img {
      width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  }

  &__thumbnail-container {
    padding: bu(1);
    background-color: map-get($background-color, darker);
  }

  &__scroller {
    display: none;

    @include breakpoint(medium) {
      padding-top: bu(2);
      display: flex;
      justify-content: center;
    }
  }

  &__mobile-back-header {
    display: flex;
    align-items: center;

    span {
      padding-left: bu(1.5);
    }
  }

  &__mobile-options {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    @include breakpoint(medium) {
      display: none;
    }

    .button-show-details {
      padding-left: bu(2.25);
      padding-right: bu(2.25);
      margin: bu(1.5) bu(2.5);

      span {
        padding-right: bu(1);
      }
    }
  }

  &__mobile-header {
    background-color: map-get($background-color, lighter);
    position: sticky;
    top: 0;
    z-index: 10;
  }

  &__mobile-content {
    background-color: map-get($background-color, default);
    bottom: 0;
    height: 100%;
    left: 0;
    overflow-x: auto;
    position: absolute;
    right: 0;
    top: 0;
  }

  &__sidebar {
    @include hide-scrollbars;
    background-color: map-get($background-color, default);
    display: flex;
    flex-direction: column;
    overflow-y: scroll;
    width: 100%;
    height: 100%;

    @include breakpoint(medium) {
      flex: 0 0 map-get($issue-ratings-width, medium);
      border-bottom-left-radius: 0;
      padding-left: bu(0.25);
      padding-right: bu(0.75);
      padding-top: bu(1.1);
      margin-top: 0;
      overflow-x: scroll;
      height: auto;
    }

    @include breakpoint(large) {
      padding-left: bu(1.25);
      padding-right: bu(1.75);
      flex: 0 0 map-get($issue-ratings-width, large);
    }
  }

  &__thumbnails {
    @include thin-scrollbars(
      map-get($background-color, default),
      map-get($background-color, lightest),
      0
    );
    display: flex;
    overflow-x: scroll;
    padding: bu(2) bu(2) bu(2) bu(2.5);
    background-color: map-get($background-color, darker);
    user-select: none;
    touch-action: pan-x;
  }

  &__thumbnail:first-of-type {
    margin-left: auto;
  }

  &__thumbnail:last-of-type {
    margin-right: auto;
  }

  &__thumbnail {
    img {
      border-radius: map-get($border-radius, mega-heavy);
      border: rem(4) solid transparent;
      cursor: pointer;
      display: block;
      min-width: map-deep-get($thumbnail-image, small, width);
      height: map-deep-get($thumbnail-image, small, height);
      object-fit: cover;

      @include breakpoint(medium) {
        max-width: map-deep-get($thumbnail-image, medium, width);
        min-height: map-deep-get($thumbnail-image, medium, height);
      }
    }

    img.selected {
      border: bu(0.5) solid map-get($foreground-color, light);
    }
  }

  &__hidden-icons {
    display: none;
  }
}
</style>

<style lang="scss">
@import "@/stylesheets/global";

.__markerjs2_toolbar_button {
  padding: bu(0.5) !important;
  margin: bu(0.5);
  border: bu(0.25) solid map-get($background-color, lightest);
  border-radius: bu(1);
}

.__markerjs2_toolbar_active_button {
  border: bu(0.25) solid map-get($foreground-color, light);
}

</style>
